import {
  parseCompleteMemberCommunicationAttachmentUploadRequest,
  parseCreateMemberCommunicationAttachmentRequest,
  parseCreateMemberCommunicationRequest,
  parseMemberCommunication,
  parseMemberCommunicationAttachment,
  parseMemberCommunicationAttachmentAccessUrlResponse,
  parseMemberCommunicationAttachmentUploadSessionResponse,
  parseMemberCommunicationThumbnailAccessUrlResponse,
  parseMemberCommunicationTargetOptionsResponse,
  parseUpdateMemberCommunicationRequest,
  parseViewerMemberCommunication,
  parseViewerMemberCommunicationsResponse
} from "@madamuh/domain";
import type {
  CompleteMemberCommunicationAttachmentUploadRequestDto,
  CreateMemberCommunicationAttachmentRequestDto,
  CreateMemberCommunicationRequestDto,
  MemberCommunicationAttachmentAccessUrlResponseDto,
  MemberCommunicationThumbnailAccessUrlResponseDto,
  MemberCommunicationAttachmentDto,
  MemberCommunicationAttachmentUploadSessionResponseDto,
  MemberCommunicationDto,
  MemberCommunicationTargetOptionsResponseDto,
  MemberCommunicationTargetType,
  UpdateMemberCommunicationRequestDto,
  ViewerMemberCommunicationDto,
  ViewerMemberCommunicationsResponseDto
} from "@madamuh/domain";
import { apiGet, apiPatch, apiPost } from "./client";

type MemberCommunicationsResponseDto = {
  items: MemberCommunicationDto[];
};

function parseMemberCommunicationsResponse(value: unknown): MemberCommunicationsResponseDto {
  const record = value as { items?: unknown };
  const items = Array.isArray(record.items) ? record.items.map((item) => parseMemberCommunication(item)) : [];
  return { items };
}

export async function fetchFamilyMemberCommunications(tenantSlug: string): Promise<ViewerMemberCommunicationsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/app/tenants/${tenantSlug}/member-communications`);
  return parseViewerMemberCommunicationsResponse(response);
}

export async function fetchFamilyMemberCommunicationDetail(
  tenantSlug: string,
  communicationId: string
): Promise<ViewerMemberCommunicationDto> {
  const response = await apiGet<unknown>(`/api/v1/app/tenants/${tenantSlug}/member-communications/${communicationId}`);
  return parseViewerMemberCommunication(response);
}

export async function markFamilyMemberCommunicationRead(
  tenantSlug: string,
  communicationId: string
): Promise<ViewerMemberCommunicationDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/app/tenants/${tenantSlug}/member-communications/${communicationId}/read`,
    {}
  );
  return parseViewerMemberCommunication(response);
}

export async function createFamilyCommunicationAttachmentAccessUrl(
  tenantSlug: string,
  communicationId: string,
  attachmentId: string
): Promise<MemberCommunicationAttachmentAccessUrlResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/app/tenants/${tenantSlug}/member-communications/${communicationId}/attachments/${attachmentId}/access-url`,
    {}
  );
  return parseMemberCommunicationAttachmentAccessUrlResponse(response);
}

export async function createFamilyCommunicationThumbnailAccessUrl(
  tenantSlug: string,
  communicationId: string,
  attachmentId: string
): Promise<MemberCommunicationThumbnailAccessUrlResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/app/tenants/${tenantSlug}/member-communications/${communicationId}/attachments/${attachmentId}/thumbnail-access-url`,
    {}
  );
  return parseMemberCommunicationThumbnailAccessUrlResponse(response);
}

export async function fetchAdminMemberCommunications(tenantSlug: string): Promise<MemberCommunicationsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/member-communications`);
  return parseMemberCommunicationsResponse(response);
}

export async function fetchAdminMemberCommunicationTargetOptions(
  tenantSlug: string,
  targetType: Exclude<MemberCommunicationTargetType, "tenant">
): Promise<MemberCommunicationTargetOptionsResponseDto> {
  const response = await apiGet<unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications/target-options?targetType=${encodeURIComponent(targetType)}`
  );
  return parseMemberCommunicationTargetOptionsResponse(response);
}

export async function fetchAdminMemberCommunicationDetail(
  tenantSlug: string,
  communicationId: string
): Promise<MemberCommunicationDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/member-communications/${communicationId}`);
  return parseMemberCommunication(response);
}

export async function createAdminMemberCommunication(
  tenantSlug: string,
  request: CreateMemberCommunicationRequestDto
): Promise<MemberCommunicationDto> {
  const parsedRequest = parseCreateMemberCommunicationRequest(request);
  const response = await apiPost<CreateMemberCommunicationRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications`,
    parsedRequest
  );
  return parseMemberCommunication(response);
}

export async function patchAdminMemberCommunication(
  tenantSlug: string,
  communicationId: string,
  request: UpdateMemberCommunicationRequestDto
): Promise<MemberCommunicationDto> {
  const parsedRequest = parseUpdateMemberCommunicationRequest(request);
  const response = await apiPatch<UpdateMemberCommunicationRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications/${communicationId}`,
    parsedRequest
  );
  return parseMemberCommunication(response);
}

export async function hideAdminMemberCommunication(tenantSlug: string, communicationId: string): Promise<MemberCommunicationDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications/${communicationId}/hide`,
    {}
  );
  return parseMemberCommunication(response);
}

export async function republishAdminMemberCommunication(tenantSlug: string, communicationId: string): Promise<MemberCommunicationDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications/${communicationId}/republish`,
    {}
  );
  return parseMemberCommunication(response);
}

export async function createAdminCommunicationAttachment(
  tenantSlug: string,
  communicationId: string,
  request: CreateMemberCommunicationAttachmentRequestDto
): Promise<MemberCommunicationAttachmentDto> {
  const parsedRequest = parseCreateMemberCommunicationAttachmentRequest(request);
  const response = await apiPost<CreateMemberCommunicationAttachmentRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications/${communicationId}/attachments`,
    parsedRequest
  );
  return parseMemberCommunicationAttachment(response);
}

export async function createAdminCommunicationAttachmentUploadSession(
  tenantSlug: string,
  communicationId: string,
  attachmentId: string
): Promise<MemberCommunicationAttachmentUploadSessionResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications/${communicationId}/attachments/${attachmentId}/upload-session`,
    {}
  );
  return parseMemberCommunicationAttachmentUploadSessionResponse(response);
}

export async function completeAdminCommunicationAttachmentUpload(
  tenantSlug: string,
  communicationId: string,
  attachmentId: string,
  request: CompleteMemberCommunicationAttachmentUploadRequestDto
): Promise<MemberCommunicationDto> {
  const parsedRequest = parseCompleteMemberCommunicationAttachmentUploadRequest(request);
  const response = await apiPost<CompleteMemberCommunicationAttachmentUploadRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications/${communicationId}/attachments/${attachmentId}/complete`,
    parsedRequest
  );
  return parseMemberCommunication(response);
}

export async function createAdminCommunicationThumbnailAccessUrl(
  tenantSlug: string,
  communicationId: string,
  attachmentId: string
): Promise<MemberCommunicationThumbnailAccessUrlResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/member-communications/${communicationId}/attachments/${attachmentId}/thumbnail-access-url`,
    {}
  );
  return parseMemberCommunicationThumbnailAccessUrlResponse(response);
}

export async function fetchTeacherMemberCommunications(tenantSlug: string): Promise<MemberCommunicationsResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/teacher/tenants/${tenantSlug}/member-communications`);
  return parseMemberCommunicationsResponse(response);
}

export async function createTeacherMemberCommunication(
  tenantSlug: string,
  request: CreateMemberCommunicationRequestDto
): Promise<MemberCommunicationDto> {
  const parsedRequest = parseCreateMemberCommunicationRequest(request);
  const response = await apiPost<CreateMemberCommunicationRequestDto, unknown>(
    `/api/v1/teacher/tenants/${tenantSlug}/member-communications`,
    parsedRequest
  );
  return parseMemberCommunication(response);
}

export async function createTeacherCommunicationAttachment(
  tenantSlug: string,
  communicationId: string,
  request: CreateMemberCommunicationAttachmentRequestDto
): Promise<MemberCommunicationAttachmentDto> {
  const parsedRequest = parseCreateMemberCommunicationAttachmentRequest(request);
  const response = await apiPost<CreateMemberCommunicationAttachmentRequestDto, unknown>(
    `/api/v1/teacher/tenants/${tenantSlug}/member-communications/${communicationId}/attachments`,
    parsedRequest
  );
  return parseMemberCommunicationAttachment(response);
}

export async function createTeacherCommunicationAttachmentUploadSession(
  tenantSlug: string,
  communicationId: string,
  attachmentId: string
): Promise<MemberCommunicationAttachmentUploadSessionResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/teacher/tenants/${tenantSlug}/member-communications/${communicationId}/attachments/${attachmentId}/upload-session`,
    {}
  );
  return parseMemberCommunicationAttachmentUploadSessionResponse(response);
}

export async function completeTeacherCommunicationAttachmentUpload(
  tenantSlug: string,
  communicationId: string,
  attachmentId: string,
  request: CompleteMemberCommunicationAttachmentUploadRequestDto
): Promise<MemberCommunicationDto> {
  const parsedRequest = parseCompleteMemberCommunicationAttachmentUploadRequest(request);
  const response = await apiPost<CompleteMemberCommunicationAttachmentUploadRequestDto, unknown>(
    `/api/v1/teacher/tenants/${tenantSlug}/member-communications/${communicationId}/attachments/${attachmentId}/complete`,
    parsedRequest
  );
  return parseMemberCommunication(response);
}

export async function createTeacherCommunicationThumbnailAccessUrl(
  tenantSlug: string,
  communicationId: string,
  attachmentId: string
): Promise<MemberCommunicationThumbnailAccessUrlResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/teacher/tenants/${tenantSlug}/member-communications/${communicationId}/attachments/${attachmentId}/thumbnail-access-url`,
    {}
  );
  return parseMemberCommunicationThumbnailAccessUrlResponse(response);
}
